import React, { useCallback, useContext, useMemo, useState } from 'react';
import './dashboard-v2.scss';
import { Location } from '../../../clients/quipli-service/location/location';
import Icon from '../../../components/icons/icon';
import { LocationSelect } from '../../location/select/location-select';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import { debounce } from 'lodash';
import { TabNavRequest } from '../../../components/tab-nav-v2/tab-nav-request';
import { TenantContext } from '../../../context/tenant-context';
import { Order } from '../../../clients/quipli-service/order/order';
import { DateTime } from 'luxon';
import { exportToCSV } from '../../order/order-utils';
import { OrderCardProcessedDetails, sortOrders } from './dashboard-utils';
import { OrderCard } from './order-card';
import { DateTimeAdvanced } from '../../date-time-advanced/date-time-advanced';
import SkeletonLoader from '../../../components/skeleton-loader/skeleton-loader';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { useDashboardData } from './hooks/use-dashboard-data';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { useDashboardQueryParams } from './hooks/use-dashboard-query-params';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Badge } from '../../../components/badge/badge';
import { safeRound } from '../../../util/util';

type KPIProps = {
  title: string;
  value: number;
  trend?: KPITrend;
  suffix?: string;
  href?: string;
};

export enum TabOptions {
  All = 'All',
  ComingIn = 'Coming in',
  GoingOut = 'Going out',
  PastDue = 'Past Due',
}

export const DashboardV2 = () => {
  const { contextPath } = useContext(TenantContext);

  // Filters
  const [selectedTab, setSelectedTab] = useState<TabOptions>(TabOptions.All);
  const [searchText, setSearchText] = useState('');
  const [from, setFrom] = useState<DateTime | null>(null);
  const [to, setTo] = useState<DateTime | null>(null);

  const { request, setRequest } = useDashboardQueryParams();

  const {
    getOrders,
    loading,
    loadingError,
    summaryLoading,
    summaryError,
    summaryData,
    ordersStartingToday,
    ordersEndingToday,
    combinedOrders,
    originalOrders,
    pastDueOrders,
    drivers,
    allLocations,
    location,
    setLocation,
    unitTrackingViaUnitNum,
  } = useDashboardData({
    from,
    to,
    searchText,
    hideActioned: request.hideActioned ?? false,
    hideQuotes: request.hideQuotes ?? false,
    hideRequests: request.hideRequests ?? false,
  });

  const KPIs: KPIProps[] = [
    {
      title: 'Fleet Utilization',
      value: summaryData['Current Utilization %'],
      suffix: '%',
    },
    {
      title: 'On Rent',
      value: summaryData['On Rent'],
      href:
        `${contextPath}manager/orders?workFlowStatus=On+Rent&pageSize=10&pageNumber=1` +
        (location ? `&locationId=${location.id}` : ''),
    },
    {
      title: 'Order Requests',
      value: summaryData['Order Requests'],
      href:
        `${contextPath}manager/orders?workFlowStatus=Requests&pageSize=10&pageNumber=1` +
        (location ? `&locationId=${location.id}` : ''),
    },
    {
      title: 'Hard Down',
      value: summaryData['Hard Down'],
      href: `${contextPath}manager/workorders` + (location ? `?locationId=${location.id}` : ''),
    },
  ];

  const renderOrders = () => {
    let orders = [];
    if (selectedTab === TabOptions.All) {
      orders = combinedOrders;
    } else if (selectedTab === TabOptions.ComingIn) {
      orders = ordersEndingToday;
    } else if (selectedTab === TabOptions.GoingOut) {
      orders = ordersStartingToday;
    } else {
      orders = pastDueOrders;
    }
    return orders;
  };

  const setSearchTextDelayed = useCallback(
    debounce((keyword: string) => setSearchText(keyword), 500),
    [],
  );

  const deliveryOrders = renderOrders().filter((order) => order.isDelivery);

  const inStoreOrders = renderOrders().filter((order) => !order.isDelivery);

  const filtersTabNames = [
    { name: TabOptions.All, count: combinedOrders ? combinedOrders.length : 0 },
    { name: TabOptions.GoingOut, count: ordersStartingToday ? ordersStartingToday.length : 0 },
    { name: TabOptions.ComingIn, count: ordersEndingToday ? ordersEndingToday.length : 0 },
    { name: TabOptions.PastDue, count: pastDueOrders ? pastDueOrders.length : 0 },
  ];

  const hideActions = [
    {
      label: 'Actioned Orders',
      value: request.hideActioned,
      onClick: () => setRequest((prev) => ({ ...prev, hideActioned: !request.hideActioned })),
    },
    {
      label: 'Quotes',
      value: request.hideQuotes,
      onClick: () => setRequest((prev) => ({ ...prev, hideQuotes: !request.hideQuotes })),
    },
    {
      label: 'Requests',
      value: request.hideRequests,
      onClick: () => setRequest((prev) => ({ ...prev, hideRequests: !request.hideRequests })),
    },
  ];

  const deliveryTabName = useMemo(() => {
    if (selectedTab === TabOptions.All || selectedTab === TabOptions.PastDue) {
      return 'Delivery/Pickup';
    } else if (selectedTab === TabOptions.ComingIn) {
      return 'Pickup';
    }
    return 'Delivery';
  }, [selectedTab]);

  const onDownload = (orders: Order[]) => {
    allLocations &&
      exportToCSV(
        orders,
        allLocations,
        `Today_Orders_${DateTime.local(
          location
            ? {
                zone: location.timezone,
              }
            : undefined,
        )
          .toFormat('D')
          .replaceAll('/', '-')}`,
        [
          'OrderNumber',
          'CustomerName',
          'RequestedTime',
          'Location',
          'Delivery',
          'RentalStart',
          'RentalEnd',
          'Products',
          'Amount',
          'Status',
          'Action',
        ],
        unitTrackingViaUnitNum ? 'unitNumber' : 'serialNumber',
      );
  };

  return (
    <div className="dashboard">
      {/* Dashboard KPI Cards */}
      <div className="dashboard-kpis grid grid-cols-2 grid-rows-2 sm:grid-cols-4 sm:grid-rows-1 mb-3 gap-3">
        {summaryError ? (
          <PrettyErrorState />
        ) : (
          <>
            {KPIs.map((kpi, index) => (
              <div
                key={index}
                className="kpi-card flex flex-col gap-1 bg-white m-[2px] p-[12px] min-h-[64px] rounded-md shadow-md "
              >
                {summaryLoading ? (
                  <SkeletonLoader key={index} style={{ height: 52 }} num={1} />
                ) : (
                  <a key={index} href={kpi.href} className="no-underline">
                    <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
                      {kpi.title}
                    </Typography>
                    <Typography type="text-lg" weight="bold" className="text-[var(--grey-900)]">
                      {`${safeRound(kpi.value) + (kpi.suffix ? kpi.suffix : '')}`}
                    </Typography>
                    {kpi.trend && <KPITrendComponent trend={kpi.trend} />}
                  </a>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Dashboard Header */}
      <div className="grid grid-cols-1 grid-rows-3 sm:grid-rows-1 sm:grid-cols-4 gap-3 dashboard-header">
        <div className="dropdown-option col-span-2 sm:col-span-1 sm:order-1 flex flex-col">
          <LocationSelect orderLocation={location as Location | undefined} setLocation={setLocation} clearable={true} />
        </div>
        <div className="dropdown-option col-span-2 sm:col-span-1 sm:order-2 flex flex-col">
          <DateTimeAdvanced setFrom={setFrom} setTo={setTo} request={request} setRequest={setRequest} />
        </div>
        <div className="header-actions col-span-2 sm:order-3 flex flex-col sm:flex-row">
          {selectedTab !== TabOptions.PastDue && (
            <Button
              className="button-secondary !border-0 h-[36px]"
              data-cy="create-order"
              onClick={() => onDownload(originalOrders)}
            >
              <Icon icon="Download" size="medium" width={'24'} height={'24'} />
              <Typography
                type="text-md"
                weight="semi-bold"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                Download all orders
              </Typography>
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard Filters */}
      <div className="flex w-full sm:items-center justify-between gap-2 mb-3 overflow-x-auto flex-col sm:flex-row">
        <TabNavRequest
          tabNames={filtersTabNames}
          selectedTab={selectedTab}
          onSelect={(tab) => {
            setSelectedTab(tab as TabOptions);
            return <></>;
          }}
          className="bg-transparent !mt-0"
        />

        <div className="flex flex-row gap-2 mt-2">
          <Dropdown
            closeOnClickInside={false}
            highlightBackground={true}
            trigger={(isOpen) => (
              <Button className="button-secondary !border-none relative">
                <Typography type="text-sm">Hide</Typography>
                {hideActions.filter((action) => action.value).length > 0 && (
                  <Badge
                    className="absolute -right-0 -top-3"
                    text={`${hideActions.filter((action) => action.value).length}`}
                    type="black"
                    size="small"
                  />
                )}
                <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
              </Button>
            )}
          >
            {hideActions.map(({ label, value, onClick }) => (
              <div
                key={`dashboard-action-${label}`}
                className="min-w-full flex justify-start items-center gap-2 px-0 cursor-pointer"
                onClick={() => {
                  onClick();
                }}
              >
                <div className="flex justify-center justify-items-center gap-1 text-[var(--quipli-black-800)]">
                  <Checkbox isChecked={value} />
                  <Typography type="text-sm">{label}</Typography>
                </div>
              </div>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-bar flex flex-row gap-2">
        <input
          type="text"
          placeholder="Search orders"
          onChange={(e) => setSearchTextDelayed(e.target.value)}
          className="w-full border border-solid border-[var(--grey-300)] rounded-md p-2"
        />
      </div>
      {/* Dashboard Content */}

      {loadingError ? (
        <PrettyErrorState className="mt-4 h-[50vh] items-center" />
      ) : (
        <>
          <div className="dashboard-content">
            <div className="grid grid-cols-1 sm:grid-rows-1 sm:grid-cols-2 mt-3 gap-3">
              <DashboardColumn
                title={deliveryTabName}
                location={location}
                onReload={getOrders}
                loading={loading}
                onDownload={() => onDownload(deliveryOrders.map((order) => order.order))}
                orders={deliveryOrders}
                drivers={drivers}
                unitTrackingViaUnitNum={unitTrackingViaUnitNum}
                selectedTab={selectedTab}
              />
              <DashboardColumn
                title="In Store"
                location={location}
                onReload={getOrders}
                loading={loading}
                onDownload={() => onDownload(inStoreOrders.map((order) => order.order))}
                orders={inStoreOrders}
                drivers={drivers}
                selectedTab={selectedTab}
                unitTrackingViaUnitNum={unitTrackingViaUnitNum}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DashboardColumn = ({
  title,
  onDownload,
  orders,
  loading,
  location,
  onReload,
  drivers,
  selectedTab,
  unitTrackingViaUnitNum = true,
}: {
  title: string;
  onDownload: () => void;
  orders: OrderCardProcessedDetails[];
  loading: boolean;
  location: Location | null | undefined;
  onReload: () => void;
  drivers: Admin[];
  selectedTab: TabOptions;
  unitTrackingViaUnitNum?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const MAX_ORDERS = 5;
  const numOrders = orders.length;
  const numOverflow = numOrders - MAX_ORDERS;

  const sortedOrders = sortOrders(orders, selectedTab);

  return (
    <div className="dashboard-column">
      <ColumnHeader
        expanded={expanded}
        setExpanded={setExpanded}
        title={title}
        onDownload={onDownload}
        count={sortedOrders.length}
      />
      {loading ? (
        <SkeletonLoader style={{ height: 270, margin: '0.5rem' }} num={2} />
      ) : (
        <>
          <div className={`${expanded ? 'block' : 'hidden'} sm:block`}>
            {sortedOrders.slice(0, !showMore ? MAX_ORDERS : undefined).map((order, index) => (
              <OrderCard
                onReload={onReload}
                orderCardDetails={{ ...order }}
                location={location}
                key={index}
                drivers={drivers}
                unitTrackingViaUnitNum={unitTrackingViaUnitNum}
              />
            ))}
          </div>
          {numOverflow > 0 && (
            <div
              className={`flex justify-center p-2 pb-3 ${expanded ? 'flex' : 'hidden'} sm:flex`}
              onClick={() => setShowMore((last) => !last)}
            >
              <Typography type="text-lg" weight="regular" className="text-grey-600 cursor-pointer underline">
                {!showMore ? `+ ${numOverflow} more` : '- Less orders'}
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ColumnHeader = ({
  title,
  onDownload,
  count,
  expanded,
  setExpanded,
}: {
  title: string;
  onDownload: () => void;
  count: number;
  expanded: boolean;
  setExpanded: any;
}) => {
  const handleClick = () => setExpanded((last: boolean) => !last);

  return (
    <div
      className="column-header flex flex-row items-center p-3 justify-between cursor-pointer sm:cursor-default"
      onClick={handleClick}
    >
      <Icon icon={expanded ? 'ChevronUp' : 'ChevronDown'} className="block sm:hidden cursor-pointer" />
      <Typography type="text-md" weight="semi-bold" className="text-[var(--grey-900)]">
        {title} ({count})
      </Typography>
      <div
        className="flex flex-row gap-1 mr-2 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onDownload();
        }}
      >
        <Icon icon="Download" size="medium" color="black" />
        <Typography type="text-sm" weight="regular" className="text-grey-700  sm:block hidden">
          Download
        </Typography>
      </div>
    </div>
  );
};

type KPITrend = {
  value: number;
  comparisonTime: string;
};

const KPITrendComponent = ({ trend }: { trend: KPITrend }) => {
  const trendDirection = trend.value > 0 ? 'up' : 'down';

  type TrendProp = {
    iconColor: string;
    numberColor: string;
    iconName: 'ArrowUp' | 'ArrowDown';
  };
  const trendProps: { [key: string]: TrendProp } = {
    up: {
      iconColor: 'var(--success-500)',
      numberColor: 'text-[var(--success-700)]',
      iconName: 'ArrowUp',
    },
    down: {
      iconColor: 'var(--error-500)',
      numberColor: 'text-[var(--error-700)]',
      iconName: 'ArrowDown',
    },
  };

  const trendT = trendProps[trendDirection];

  return (
    <div className="flex items-center gap-1">
      <Icon icon={trendT.iconName} color={trendT.iconColor} size="small" />
      <Typography type="text-sm" className={trendT.numberColor}>
        {trend.value && `${safeRound(trend.value * 100)}%`}
      </Typography>
      <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
        {trend.comparisonTime}
      </Typography>
    </div>
  );
};

const PrettyErrorState = ({ className = '' }: { className?: string }) => {
  return (
    <div className={`flex bg-white justify-center p-6 gap-4 shadow-md rounded-md col-span-4 ${className}`}>
      <Icon icon="AlertTriangle" />
      <Typography type="text-md" weight="bold">
        Something went wrong, please refresh the page and try again.
      </Typography>
    </div>
  );
};
